import { DistributiveOmit } from '../../../../helpers/discriminatedUnionHelpers';

export enum TreeNodeType {
  HeatSource = 'HEAT_SOURCE',
  Pump = 'PUMP',
  Junction = 'JUNCTION',
  Manifold = 'MANIFOLD',
}

export type NodeState = 'OK' | 'Error' | 'Warning';

export type HeatSourceTreeNode = {
  nodeType: TreeNodeType.HeatSource;
  id: string;
  markedForDeletion: boolean;
  firstChild: Child;
  fieldData: {
    heatSource: string | null;
  };
  stats: {
    generatedPressureInKilopascals: number | null;
    exitPipe: PipeStats;
    indexCircuitStats: PressureStats;
    state: NodeState;
  };
};

export type JunctionTreeNode = {
  nodeType: TreeNodeType.Junction;
  id: string;
  markedForDeletion: boolean;
  firstChild: Child;
  secondChild: Child;
  fieldData: null | {
    manifoldIds: Array<string>;
  };
  stats: {
    flowRateInLitresPerMinute: number | null;
    firstChildExitPipe: PipeStats;
    secondChildExitPipe: PipeStats;
  };
};

export type ManifoldTreeNode = {
  nodeType: TreeNodeType.Manifold;
  id: string;
  markedForDeletion: boolean;
  fieldData: {
    pumpOnManifold: boolean | null;
    manifoldIndex: number | null;
  };
  stats: {
    pumpToUfh: PressureStats;
    boilerToBlendingValve: PressureStats | null;
    flowRateInLitresPerMinute: number | null;
    pressureDropInKilopascals: number | null;
    state: NodeState;
  };
};

export type PressureStats = {
  requiredPressureInKilopascals: number | null;
  availablePressureInKilopascals: number | null;
  pumpCapacityPercentage: number | null;
};

export type PumpTreeNode = {
  nodeType: TreeNodeType.Pump;
  id: string;
  markedForDeletion: boolean;
  firstChild: Child;
  fieldData: {
    pump: string | null;
    includesBlendingValve: boolean | null;
  };
  stats: {
    generatedPressureInKilopascals: number | null;
    exitPipe: PipeStats;
    indexCircuitStats: PressureStats;
    state: NodeState;
  };
};

export type PipeFields = {
  code: string | null;
  lengthInMetres: number | null;
  markedForDeletion: boolean;
};

export type PipeStats = {
  flowRateInLitresPerMinute: number | null;
  velocityInMetresPerSecond: number | null;
  pressureDropInKilopascals: number | null;
  state: NodeState;
};

type Child = {
  id: string | null;
  exitPipe: PipeFields;
};

export const NULL_CHILD: Child = {
  id: null,
  exitPipe: {
    code: '22mm C',
    lengthInMetres: null,
    markedForDeletion: false,
  },
};

export type TreeNode = DistributiveOmit<TreeNodeWithStats, 'stats'>;
export type TreeNodeWithStats = HeatSourceTreeNode | JunctionTreeNode | ManifoldTreeNode | PumpTreeNode;
export type TreeNodeWithMapNodeEquivalent = HeatSourceTreeNode | ManifoldTreeNode | PumpTreeNode;
export type NewTreeNode = DistributiveOmit<TreeNode, 'id'>;

export type TreeNodeWithSingleChild = HeatSourceTreeNode | PumpTreeNode;
export type TreeNodeWithChildren = TreeNodeWithSingleChild | JunctionTreeNode;
